import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  XCircle, 
  DollarSign, 
  Package, 
  ArrowLeftRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  MessageSquare,
  Calendar,
  CalendarCheck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const CustomerRequests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'cancellations';
  
  const [cancellations, setCancellations] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [replacements, setReplacements] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [bookingCancellations, setBookingCancellations] = useState([]);
  const [bookingReschedules, setBookingReschedules] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [responseModal, setResponseModal] = useState({ open: false, request: null, type: null });
  const [vendorNotes, setVendorNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [cancRes, refRes, repRes, excRes, bookingRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/cancellations?status=pending_vendor_approval`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/refunds?status=pending_vendor_approval`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/replacements?status=pending_vendor_approval`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/exchanges?status=pending_vendor_approval`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/booking-requests`, { headers })
      ]);
      
      if (cancRes.ok) setCancellations(await cancRes.json());
      if (refRes.ok) setRefunds(await refRes.json());
      if (repRes.ok) setReplacements(await repRes.json());
      if (excRes.ok) setExchanges(await excRes.json());
      
      if (bookingRes.ok) {
        const bookingData = await bookingRes.json();
        setBookingCancellations(bookingData.cancellations || []);
        setBookingReschedules(bookingData.reschedules || []);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (action) => {
    if (!responseModal.request) return;
    
    setProcessing(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Determine the correct endpoint based on request type
      let endpoint;
      if (responseModal.type === 'booking-cancellations' || responseModal.type === 'booking-reschedules') {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/booking-requests/${responseModal.request.request_id}/respond`;
      } else {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/customer-requests/vendor/${responseModal.type}/${responseModal.request.request_id}/respond`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_id: responseModal.request.request_id,
          request_type: responseModal.type.replace('booking-', 'booking_').slice(0, -1), // Handle booking types
          action: action,
          vendor_notes: vendorNotes
        })
      });
      
      if (response.ok) {
        // Success! Refresh the list
        await fetchAllRequests();
        setResponseModal({ open: false, request: null, type: null });
        setVendorNotes('');
        
        // Show success message
        alert(`Request ${action}d successfully!`);
      } else {
        alert('Failed to process request. Please try again.');
      }
    } catch (error) {
      console.error('Error processing request:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const RequestCard = ({ request, type }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'pending_vendor_approval': return 'yellow';
        case 'approved': return 'green';
        case 'rejected': return 'red';
        default: return 'gray';
      }
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const isBookingRequest = type === 'booking-cancellations' || type === 'booking-reschedules';

    return (
      <Card className="mb-4 hover:shadow-md transition-shadow">
        <CardContent className="pt-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Badge variant={getStatusColor(request.request_status)}>
                  {request.request_status.replace('_', ' ').toUpperCase()}
                </Badge>
                <span className="text-sm text-gray-500">
                  Requested {formatDate(request.requested_at)}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{isBookingRequest ? 'Booking ID:' : 'Order ID:'}</span>
                  <span className="text-sm text-gray-900 font-mono">{request.booking_id || request.order_id}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Customer:</span>
                  <span className="text-sm text-gray-900">{request.customer_name} ({request.customer_email})</span>
                </div>
                
                <div className="flex items-start gap-2">
                  <span className="text-sm font-medium text-gray-700">Reason:</span>
                  <div>
                    <Badge variant="outline" className="mb-1">{request.reason_category || request.reason}</Badge>
                    {request.detailed_reason && (
                      <p className="text-sm text-gray-600 mt-1">{request.detailed_reason}</p>
                    )}
                  </div>
                </div>
                
                {type === 'refunds' && request.refund_type && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Type:</span>
                    <Badge>{request.refund_type.toUpperCase()}</Badge>
                  </div>
                )}
                
                {type === 'booking-reschedules' && request.requested_new_date && (
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-700">Requested Date:</span>
                    <div>
                      <p className="text-sm text-gray-900">{formatDate(request.requested_new_date)}</p>
                      {request.original_date && (
                        <p className="text-xs text-gray-500 mt-1">Original: {formatDate(request.original_date)}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {type === 'booking-cancellations' && request.cancellation_fee && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Cancellation Fee:</span>
                    <Badge variant="destructive">₹{request.cancellation_fee.toFixed(2)}</Badge>
                  </div>
                )}
                
                {request.images_uploaded && request.images_uploaded.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-gray-700 block mb-2">Customer Images:</span>
                    <div className="flex gap-2 flex-wrap">
                      {request.images_uploaded.map((img, idx) => (
                        <img 
                          key={img.id || img.name || `${img.title || "img"}-${idx}`}
                          src={img}
                          alt={`Evidence ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded border cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => window.open(img, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col gap-2 ml-4">
              <Button
                onClick={() => setResponseModal({ open: true, request, type })}
                className="bg-green-600 hover:bg-green-700"
                size="sm"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button
                onClick={() => setResponseModal({ open: true, request, type })}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                size="sm"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              {!isBookingRequest && (
                <Button
                  onClick={() => window.open(`/vendor/orders/${request.order_id}`, '_blank')}
                  variant="ghost"
                  size="sm"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View Order
                </Button>
              )}
              {isBookingRequest && (
                <Button
                  onClick={() => window.open(`/vendor/bookings/${request.booking_id}`, '_blank')}
                  variant="ghost"
                  size="sm"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View Booking
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-3 md:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 md:p-6">
        <div className="mb-6">
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Customer Requests</h1>
          <p className="text-gray-600 mt-2">Review and respond to customer requests</p>
        </div>

      <Tabs value={activeTab} onValueChange={(val) => setSearchParams({ tab: val })}>
        <TabsList className="grid grid-cols-3 lg:grid-cols-6 lg:w-full gap-2">
          <TabsTrigger value="cancellations" className="flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            <span className="hidden lg:inline">Cancellations</span>
            <span className="lg:hidden">Cancel</span>
            {cancellations.length > 0 && (
              <Badge variant="destructive" className="ml-1">{cancellations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="refunds" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden lg:inline">Refunds</span>
            <span className="lg:hidden">Refund</span>
            {refunds.length > 0 && (
              <Badge variant="destructive" className="ml-1">{refunds.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="replacements" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden lg:inline">Replacements</span>
            <span className="lg:hidden">Replace</span>
            {replacements.length > 0 && (
              <Badge variant="destructive" className="ml-1">{replacements.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="exchanges" className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            <span className="hidden lg:inline">Exchanges</span>
            <span className="lg:hidden">Exchange</span>
            {exchanges.length > 0 && (
              <Badge variant="destructive" className="ml-1">{exchanges.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="booking-cancellations" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden lg:inline">Booking Cancel</span>
            <span className="lg:hidden">B-Cancel</span>
            {bookingCancellations.length > 0 && (
              <Badge variant="destructive" className="ml-1">{bookingCancellations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="booking-reschedules" className="flex items-center gap-2">
            <CalendarCheck className="h-4 w-4" />
            <span className="hidden lg:inline">Booking Resched</span>
            <span className="lg:hidden">B-Resched</span>
            {bookingReschedules.length > 0 && (
              <Badge variant="destructive" className="ml-1">{bookingReschedules.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cancellations" className="mt-6">
          {cancellations.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No pending cancellation requests</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {cancellations.map(request => (
                <RequestCard key={request.request_id} request={request} type="cancellations" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="refunds" className="mt-6">
          {refunds.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No pending refund requests</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {refunds.map(request => (
                <RequestCard key={request.request_id} request={request} type="refunds" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="replacements" className="mt-6">
          {replacements.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No pending replacement requests</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {replacements.map(request => (
                <RequestCard key={request.request_id} request={request} type="replacements" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="exchanges" className="mt-6">
          {exchanges.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No pending exchange requests</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {exchanges.map(request => (
                <RequestCard key={request.request_id} request={request} type="exchanges" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="booking-cancellations" className="mt-6">
          {bookingCancellations.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No pending booking cancellation requests</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {bookingCancellations.map(request => (
                <RequestCard key={request.request_id} request={request} type="booking-cancellations" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="booking-reschedules" className="mt-6">
          {bookingReschedules.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No pending booking reschedule requests</p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {bookingReschedules.map(request => (
                <RequestCard key={request.request_id} request={request} type="booking-reschedules" />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Response Modal */}
      <Dialog open={responseModal.open} onOpenChange={(open) => setResponseModal({ ...responseModal, open })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Respond to Customer Request</DialogTitle>
            <DialogDescription>
              Provide your decision and optional notes for the customer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Vendor Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes or explanation for your decision..."
                value={vendorNotes}
                onChange={(e) => setVendorNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setResponseModal({ open: false, request: null, type: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => handleRespond('reject')}
              disabled={processing}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject Request
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleRespond('approve')}
              disabled={processing}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CustomerRequests;
